import { FileFillerInterface } from '../../interfaces/file-filler.interface';
import { CalculationResponse } from '../../responses/calculation.response';
import { ExportFileInterface } from '../../interfaces/export-file.interface';
import { ExportTypeEnum } from '../../enums/export-type.enum';
import { Section84_1Options } from '../../options/calculations/section-84_1/section-84_1.options';
import { Section84_1MetadataOptions } from '../../options/calculations/section-84_1/section-84_1-metadata.options';
import { Section84_1ShareClassOptions } from '../../options/calculations/section-84_1/section-84_1-share-class.options';
import { Workbook, Worksheet } from 'exceljs';
import { FieldTypeEnum } from '../../enums/field-type.enum';
import { AdvancedFormMetadata } from '@magieno/common';

enum ColIndexEnum {
  TITLE = 'A',
  QUESTION = 'B',
  ANSWER = 'C'
}

export class Section84_1XlsxFiller implements FileFillerInterface {

  private rowIndex: number;

  constructor() {
    this.rowIndex = 1;
  }

  async fill(calculation: CalculationResponse): Promise<ExportFileInterface> {
    return new Promise<ExportFileInterface>(async (resolve, reject) => {
      // todo: Workbook makes rollup hang forever
      const workbook = new Workbook();
      const sheet = workbook.addWorksheet('Options', {});

      this.mapOptionsQuestionsSection1(calculation, sheet);
      this.mapOptionsQuestionsSection2(calculation, sheet);
      this.mapOptionsQuestionsSection3(calculation, sheet);
      this.mapOptionsQuestionsSection4(calculation, sheet);
      this.mapOptionsQuestionsSection5(calculation, sheet);

      sheet.getColumn('B').width = 64;

      const buffer = await workbook.xlsx.writeBuffer();
      console.log('WRITING FILE');
      await workbook.xlsx.writeFile('./tmp/test4.xlsx');

      return resolve({
        fileName: `${calculation.id}.xlsx`,
        fileBytes: new Buffer(''),
        fileType: ExportTypeEnum.XLSX,
      });
    });

  }

  mapOptionsQuestionsSection1(calculation: CalculationResponse, sheet: Worksheet, locale: string = 'fr'): Worksheet {
    const template = new Section84_1MetadataOptions();
    const template2 = new Section84_1Options();
    const options = calculation.options?.options as Section84_1Options;

    // Titre
    this.addValueToSheet(sheet, ColIndexEnum.TITLE, this.rowIndex, 'Détermination de l’applicabilité du calcul “vente d’actions”');
    this.rowIndex += 1;

    // Q1
    this.addValueToSheet(sheet, ColIndexEnum.QUESTION, this.rowIndex, AdvancedFormMetadata.getAdvancedFormControl(template, 'isRollover').displayableElements?.labelTitle?.[locale] ?? '');
    this.addValueToSheet(sheet, ColIndexEnum.ANSWER, this.rowIndex, undefined); // C'est ou ca dans les options ?
    this.rowIndex += 1;

    // Q2
    const keys = ['isBuyerTheCorporationWhoseSharesAreBeingSold'];
    for (const key of keys) {
      if (AdvancedFormMetadata.hasAdvancedFormControl(template2, key) && options.hasOwnProperty(key)) {
        const advancedFormControlMetadata = AdvancedFormMetadata.getAdvancedFormControl(template2, key);
        this.addValueToSheet(sheet, ColIndexEnum.QUESTION, this.rowIndex, advancedFormControlMetadata.displayableElements?.labelTitle?.[locale] ?? '');
        this.addValueToSheet(sheet, ColIndexEnum.ANSWER, this.rowIndex, options[key], advancedFormControlMetadata.fieldType as FieldTypeEnum);
        this.rowIndex += 1;
      }
    }

    return sheet;
  }

  mapOptionsQuestionsSection2(calculation: CalculationResponse, sheet: Worksheet, locale: string = 'fr'): Worksheet {
    const template = new Section84_1Options();
    const options = calculation.options?.options as Section84_1Options;

    // Titre
    this.addValueToSheet(sheet, ColIndexEnum.TITLE, this.rowIndex, 'Informations de base sur les actions vendues');
    this.rowIndex += 1;

    const keys = ['proceedsOfDisposition', 'adjustedCostBaseOfTransferredShares', 'sellingFees'];
    for (const key of keys) {
      if (AdvancedFormMetadata.hasAdvancedFormArray(template, key) && options.hasOwnProperty(key)) {
        const advancedFormControlMetadata = AdvancedFormMetadata.getAdvancedFormControl(template, key);
        this.addValueToSheet(sheet, ColIndexEnum.QUESTION, this.rowIndex, advancedFormControlMetadata.displayableElements?.labelTitle?.[locale] ?? '');
        this.addValueToSheet(sheet, ColIndexEnum.ANSWER, this.rowIndex, options[key], advancedFormControlMetadata.fieldType as FieldTypeEnum);
        this.rowIndex += 1;
      }
    }

    return sheet;
  }

  mapOptionsQuestionsSection3(calculation: CalculationResponse, sheet: Worksheet, locale: string = 'fr'): Worksheet {
    const template = new Section84_1Options();
    const options = calculation.options?.options as Section84_1Options;

    // Titre
    this.addValueToSheet(sheet, ColIndexEnum.TITLE, this.rowIndex, 'Conditions d’application de l’article 84.1 LIR');
    this.rowIndex += 1;

    const keys = ['areTransferredSharesCapitalPropertyForTheSeller', 'isSellerAnIndividualOrATrust', 'isSellerACanadianResident', 'isBuyerACorporation', 'areBuyerAndSellerAtArmsLength', 'isCorporationForWhichSharesAreBeingSoldBecomingAtArmsLengthImmediatelyAfterTransaction'];
    for (const key of keys) {
      if (AdvancedFormMetadata.hasAdvancedFormArray(template, key) && options.hasOwnProperty(key)) {
        const advancedFormControlMetadata = AdvancedFormMetadata.getAdvancedFormControl(template, key);
        this.addValueToSheet(sheet, ColIndexEnum.QUESTION, this.rowIndex, advancedFormControlMetadata.displayableElements?.labelTitle?.[locale] ?? '');
        this.addValueToSheet(sheet, ColIndexEnum.ANSWER, this.rowIndex, options[key], advancedFormControlMetadata.fieldType as FieldTypeEnum);
        this.rowIndex += 1;
      }
    }

    return sheet;
  }

  mapOptionsQuestionsSection4(calculation: CalculationResponse, sheet: Worksheet, locale: string = 'fr'): Worksheet {
    const template = new Section84_1Options();
    const options = calculation.options?.options as Section84_1Options;

    // Titre
    this.addValueToSheet(sheet, ColIndexEnum.TITLE, this.rowIndex, 'Informations si application de l’article 84.1 LIR');
    this.rowIndex += 1;

    const keys = ['paidUpCapitalOfGiftedShares',
      'nonShareConsideration',
      'capitalGainsOnTheShareDeclaredAfter1984',
      'wereSharesAcquiredAfter1971',
      'fairMarketValueOnValuationDayOfTransferredShares',
      'actualCostOnJanuary1972OfTransferredShares',
      'receivedDividendChoice83OnTransferredShares'];

    for (const key of keys) {
      if (AdvancedFormMetadata.hasAdvancedFormControl(template, key) && options.hasOwnProperty(key)) {
        const advancedFormControlMetadata = AdvancedFormMetadata.getAdvancedFormControl(template, key);
        this.addValueToSheet(sheet, ColIndexEnum.QUESTION, this.rowIndex, advancedFormControlMetadata.displayableElements?.labelTitle?.[locale] ?? '');
        this.addValueToSheet(sheet, ColIndexEnum.ANSWER, this.rowIndex, options[key], advancedFormControlMetadata.fieldType as FieldTypeEnum);
        this.rowIndex += 1;
      }
    }

    return sheet;
  }

  mapOptionsQuestionsSection5(calculation: CalculationResponse, sheet: Worksheet, locale: string = 'fr'): Worksheet {
    const template = new Section84_1ShareClassOptions();
    const options = (calculation.options?.options as Section84_1Options).shareClasses;

    // Titre
    this.addValueToSheet(sheet, ColIndexEnum.TITLE, this.rowIndex, 'Categories d\'action');
    this.rowIndex += 1;

    const keys = [
      'title',
      'initialPaidUpCapital',
      'numberOfSharesIssued',
      'numberOfSharesBeforeShareIssuance',
      'increasePaidUpCapitalDueToShareIssuance',
      'initialAdjustedCostBase',
      'fairMarketValueOfSharesIssued',
    ];

    for (let i = 0; i < options.length; i++) {
      this.addValueToSheet(sheet, ColIndexEnum.TITLE, this.rowIndex, `Categories d'action #${i + 1}`);
      this.rowIndex += 1;

      for (const key of keys) {
        if (AdvancedFormMetadata.hasAdvancedFormControl(template, key) && options[i].hasOwnProperty(key)) {
          const advancedFormControlMetadata = AdvancedFormMetadata.getAdvancedFormControl(template, key);
          this.addValueToSheet(sheet, ColIndexEnum.QUESTION, this.rowIndex, advancedFormControlMetadata.displayableElements?.labelTitle?.[locale] ?? '');
          this.addValueToSheet(sheet, ColIndexEnum.ANSWER, this.rowIndex, options[i][key], advancedFormControlMetadata.fieldType as FieldTypeEnum);
          this.rowIndex += 1;
        }
      }
    }

    return sheet;
  }

  private addValueToSheet(sheet: Worksheet, col: ColIndexEnum, row: number, value: any, fieldType?: FieldTypeEnum) {
    // console.log(cell)
    sheet.getCell(`${col}${row}`).value = value;
    if (col === ColIndexEnum.TITLE) {
      sheet.mergeCells(`A${this.rowIndex}:C${this.rowIndex}`);
      sheet.getCell(`${col}${row}`).alignment = { wrapText: true };
      sheet.getCell(`${col}${row}`).style.font = {
        bold: true,
        size: 16,
      };
    }

    if (col === ColIndexEnum.QUESTION) {
      sheet.getCell(`${col}${row}`).alignment = { wrapText: true };
    }

    if (col === ColIndexEnum.ANSWER) {
      if (fieldType === FieldTypeEnum.Money) {
        sheet.getCell(`${col}${row}`).value = value / 100;
        sheet.getCell(`${col}${row}`).style.numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00';
      }
    }
  }

}
